import { useTranslation } from 'react-i18next';
import { Typography, Box } from '@mui/material';
import { useSelector } from 'react-redux';

import {
  selectActiveHeader,
  selectPerPayPeriodCost,
} from '../EnrollmentNavigation/services';
import {
  BenefitCategorieDetailsBoxStyled,
  CheckCircleStyled,
  CostPerPayPeriodBoxStyled,
  ProhibitStyled,
  PsDrawerDialogStyled,
} from './CartDrawerStyled';
import { benefitCategorySelector } from 'enrollment/services/BenefitCategories/selectors';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { BenefitCategory } from 'theSource/queries';
import { selectEnrollmentLatest } from 'enrollment/components/BottomNavigation/services';
import { selectPerPayPeriodText } from 'enrollment/services';
import { getPremiumStyled, RETURN_DISPLAY } from 'enrollment/utils';
import { benefitSelector } from '../../../../services/Benefit/selectors';
import {
  BenefitDto,
  CategoryResponseDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import { DETAILS_IN_ORDER } from 'enrollment/components/BottomNavigation/utils';
import { organizeBundledBenefitCategoryBenefits } from 'enrollment/utils/bundledBenefitsUtils';

interface CartDrawerProps {
  setCartDrawerDetails: (value: boolean) => void;
}

export const CartDrawer = ({ setCartDrawerDetails }: CartDrawerProps) => {
  const { t } = useTranslation('enrollment.cartDrawer');

  const activeHeader = useSelector(selectActiveHeader);
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const enrollmentLatest = useSelector(selectEnrollmentLatest);
  const cartData = useSelector(benefitSelector) as CategoryResponseDto;
  const perPayPeriodCost = useSelector(selectPerPayPeriodCost);
  const perPayText = useSelector(selectPerPayPeriodText);

  // get seen categories to display or all categories if Life Event
  const benefitCategories = useSelector(benefitCategorySelector);
  const finishedBenefitSection = ['details', 'confirm', 'review']
    .concat(DETAILS_IN_ORDER)
    .includes(enrollmentLatest);

  let index = undefined;
  if (!finishedBenefitSection) {
    index = benefitCategories.findIndex(
      (category) => category.lookup_code === enrollmentLatest
    );
  }
  let showCategories = benefitCategories.flatMap(
    (category: BenefitCategory) => category.lookup_code
  );

  if (enrollmentContext !== 'life_event' && index) {
    showCategories = showCategories?.filter((_, i) => i <= index);
  }

  const onClose = () => {
    setCartDrawerDetails(false);
  };

  return (
    <PsDrawerDialogStyled
      dialogId="ps-cart-drawer"
      closeButtonLabel={t('common:close')}
      dialogFooter={true}
      closeButtonMethod={onClose}
      data-testid="ps-cart-drawer"
      dialogHeading={t('enrollment.cartDrawer:title')}
    >
      <Box>
        <CostPerPayPeriodBoxStyled className="mt-2">
          {getPremiumStyled({
            premium: String(perPayPeriodCost),
            valueOnly: RETURN_DISPLAY,
            perPayText: perPayText,
            tagName: 'div',
            dollarClasses: 'headline-3 semiBold',
          })}
        </CostPerPayPeriodBoxStyled>

        {cartData && (
          <Box className="mt-5">
            {benefitCategories?.map((category: BenefitCategory) => {
              const lookupCode =
                showCategories.includes(category?.lookup_code) &&
                category?.lookup_code;

              let findDisplayPlans = cartData?.data?.benefits?.filter(
                (details: BenefitDto) => {
                  const findDisplayPlansCondition =
                    Boolean(details?.coverage) &&
                    details?.benefit.org_benefit_category === lookupCode;

                  return findDisplayPlansCondition;
                }
              );

              if (
                ['chosenBundle', 'otherBundle'].includes(category.lookup_code)
              ) {
                findDisplayPlans = organizeBundledBenefitCategoryBenefits(
                  cartData.data.benefits,
                  category.lookup_code as 'chosenBundle' | 'otherBundle',
                  false,
                  cartData.data.bundled_org_benefit_ids
                );
              }

              return (
                <Box key={category.id}>
                  {Boolean(findDisplayPlans) || activeHeader > 0 ? (
                    <Box>
                      <h2 className="mb-4">{category?.name}</h2>
                      {findDisplayPlans?.length !== 0 && (
                        <Box className="mb-5" key={`inner-box-${category.id}`}>
                          {findDisplayPlans?.map((item: any, index: any) => {
                            const coverageLookUpcode =
                              item?.coverage?.req_coverage_level_lookup_code;
                            const declinePlan =
                              coverageLookUpcode === 'decline';
                            const startDate = new Date(
                              item?.coverage?.change_effective_date || ''
                            );
                            const terminationDate = new Date(
                              item?.coverage?.termination_date || ''
                            );
                            const isTerminated = Boolean(
                              terminationDate <= new Date() ||
                                startDate >= terminationDate
                            );

                            const isSurvey =
                              item?.coverage?.req_coverage_level_lookup_code ===
                              'completed';

                            if (isTerminated) {
                              return null;
                            }

                            return (
                              <Box
                                key={`${item?.org_benefit_id}-${index}`}
                                className="mb-4"
                              >
                                <Box className="d-flex">
                                  <Box className="mt-n1">
                                    {!declinePlan || isSurvey ? (
                                      <CheckCircleStyled size={32} />
                                    ) : (
                                      <ProhibitStyled
                                        alt="Declined icon"
                                        size={32}
                                      />
                                    )}
                                  </Box>
                                  <BenefitCategorieDetailsBoxStyled>
                                    <h3>
                                      {!declinePlan && !isSurvey
                                        ? item?.coverage?.req_org_plan_name
                                        : item?.benefit?.org_benefit_name}
                                    </h3>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                    >
                                      {!declinePlan &&
                                        item?.coverage?.carrier_name}
                                    </Typography>
                                  </BenefitCategorieDetailsBoxStyled>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </PsDrawerDialogStyled>
  );
};
